import { Head, Link, useForm, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { useEffect, useState } from 'react'
import { Settings, Mail, Zap, Plus, Edit2, Trash2, GripVertical, Play, AlertCircle, X, Loader2, Send, Download } from 'lucide-react'

interface WorkflowStep {
  id: number
  name: string
  step_type: string
  step_type_label: string
  sort_order: number
  is_required: boolean
  is_active: boolean
  description: string | null
}

interface NotificationTemplate {
  id: number
  name: string
  template_key: string
  channel: string
  subject: string | null
  body: string
  variables: string[]
  is_active: boolean
}

interface AutomationRule {
  id: number
  name: string
  description: string | null
  trigger_type: string
  trigger_label: string
  channel: string
  notification_template_id: number | null
  notification_template_name: string | null
  timing: string
  timing_description: string
  offset_days: number
  is_active: boolean
}

interface StepType {
  value: string
  label: string
}

interface TriggerType {
  value: string
  label: string
}

interface Props extends PageProps {
  workflow_steps: WorkflowStep[]
  notification_templates: NotificationTemplate[]
  automation_rules: AutomationRule[]
  step_types: StepType[]
  trigger_types: TriggerType[]
  available_variables: string[]
  integrations: {
    sendgrid_configured: boolean
    sendgrid_blocking_reason?: string | null
  }
  missing_default_templates: string[]
}

const TAB_IDS = ['onboarding', 'email', 'automations'] as const

type TabType = (typeof TAB_IDS)[number]

const DEFAULT_TAB: TabType = 'onboarding'

// The active tab lives in the URL (?tab=email) so the page can be linked to
// straight from wherever the problem was reported, and so reload/back land on
// the tab the admin was actually looking at.
function tabFromUrl(url: string): TabType {
  const requested = new URLSearchParams(url.split('?')[1] ?? '').get('tab')
  return TAB_IDS.includes(requested as TabType) ? (requested as TabType) : DEFAULT_TAB
}

export default function WorkflowSettings({
  workflow_steps,
  notification_templates,
  automation_rules,
  step_types,
  trigger_types,
  available_variables,
  integrations,
  missing_default_templates
}: Props) {
  const { url } = usePage()
  const urlTab = tabFromUrl(url)
  const [activeTab, setActiveTab] = useState<TabType>(urlTab)

  // The URL is the source of truth; local state only makes the click feel
  // instant while the visit that rewrites the address is in flight.
  useEffect(() => {
    setActiveTab(urlTab)
  }, [urlTab])

  const selectTab = (tab: TabType) => {
    setActiveTab(tab)
    router.get('/settings/workflows', { tab }, { preserveState: true, preserveScroll: true })
  }

  const tabs = [
    { id: 'onboarding' as TabType, label: 'Onboarding Flow', icon: Settings },
    { id: 'email' as TabType, label: 'Email Templates', icon: Mail },
    { id: 'automations' as TabType, label: 'Automations', icon: Zap }
  ]

  const emailTemplates = notification_templates.filter(t => t.channel === 'email')

  return (
    <AppLayout>
      <Head title="Workflow Settings" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
              Workflow Settings
            </h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              Configure agent onboarding, notifications, and automations
            </p>
          </div>
          <Link href="/settings" className="btn-secondary">
            Back to Settings
          </Link>
        </div>

        {!integrations.sendgrid_configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-amber-800">Email will not be sent</h3>
              <p className="text-sm text-amber-700 mt-1">
                {integrations.sendgrid_blocking_reason ||
                  'SendGrid is not configured. Go to Settings → Integrations to set it up for email notifications to work.'}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
          <div className="border-b border-chestnut-charcoal/10">
            <nav className="flex -mb-px">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => selectTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-chestnut-forest text-chestnut-forest'
                        : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal hover:border-chestnut-charcoal/20'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'onboarding' && (
              <OnboardingTab steps={workflow_steps} stepTypes={step_types} />
            )}
            {activeTab === 'email' && (
              <TemplatesTab 
                templates={emailTemplates} 
                channel="email" 
                availableVariables={available_variables}
                isConfigured={integrations.sendgrid_configured}
                missingDefaults={missing_default_templates}
              />
            )}
            {activeTab === 'automations' && (
              <AutomationsTab 
                rules={automation_rules} 
                triggerTypes={trigger_types}
                templates={notification_templates}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

function OnboardingTab({ steps, stepTypes }: { steps: WorkflowStep[], stepTypes: StepType[] }) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null)

  const form = useForm({
    name: '',
    step_type: 'invitation',
    sort_order: steps.length,
    is_required: true,
    is_active: true,
    description: ''
  })

  const editForm = useForm({
    name: '',
    step_type: '',
    is_required: true,
    is_active: true,
    description: ''
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    form.post('/settings/workflows/steps', {
      onSuccess: () => {
        setIsAdding(false)
        form.reset()
      }
    })
  }

  const handleEdit = (step: WorkflowStep) => {
    setEditingStep(step)
    editForm.setData({
      name: step.name,
      step_type: step.step_type,
      is_required: step.is_required,
      is_active: step.is_active,
      description: step.description || ''
    })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingStep) return
    editForm.patch(`/settings/workflows/steps/${editingStep.id}`, {
      onSuccess: () => {
        setEditingStep(null)
        editForm.reset()
      }
    })
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this step?')) {
      router.delete(`/settings/workflows/steps/${id}`)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-chestnut-charcoal">Onboarding Steps</h2>
          <p className="text-sm text-chestnut-charcoal/60">Define the steps agents go through during onboarding</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Step
        </button>
      </div>

      {isAdding && (
        <form onSubmit={handleCreate} className="bg-chestnut-cream/50 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Step Name</label>
              <input
                type="text"
                value={form.data.name}
                onChange={e => form.setData('name', e.target.value)}
                className="input w-full"
                placeholder="e.g., Sign Contract"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Step Type</label>
              <select
                value={form.data.step_type}
                onChange={e => form.setData('step_type', e.target.value)}
                className="input w-full"
              >
                {stepTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Description</label>
            <textarea
              value={form.data.description}
              onChange={e => form.setData('description', e.target.value)}
              className="input w-full"
              rows={2}
              placeholder="Optional description of this step"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.data.is_required}
                onChange={e => form.setData('is_required', e.target.checked)}
                className="rounded border-chestnut-charcoal/30"
              />
              <span className="text-sm text-chestnut-charcoal">Required step</span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setIsAdding(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={form.processing}>Save Step</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {steps.length === 0 ? (
          <div className="text-center py-8 text-chestnut-charcoal/60">
            No onboarding steps configured. Add your first step to get started.
          </div>
        ) : (
          steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center gap-4 p-4 rounded-lg border ${
                step.is_active ? 'bg-white border-chestnut-charcoal/10' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2 text-chestnut-charcoal/40">
                <GripVertical className="h-5 w-5 cursor-move" />
                <span className="text-sm font-medium w-6">{index + 1}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-chestnut-charcoal">{step.name}</span>
                  {step.is_required && (
                    <span className="text-xs px-2 py-0.5 bg-chestnut-gold/20 text-chestnut-gold rounded-full">Required</span>
                  )}
                  {!step.is_active && (
                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">Inactive</span>
                  )}
                </div>
                <p className="text-sm text-chestnut-charcoal/60">{step.step_type_label}</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleEdit(step)}
                  className="p-2 text-chestnut-charcoal/40 hover:text-chestnut-forest rounded-lg hover:bg-chestnut-cream/50"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(step.id)} className="p-2 text-chestnut-charcoal/40 hover:text-red-600 rounded-lg hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {editingStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-chestnut-charcoal/10">
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Edit Onboarding Step</h3>
              <button onClick={() => setEditingStep(null)} className="p-1 text-chestnut-charcoal/40 hover:text-chestnut-charcoal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Step Name</label>
                <input
                  type="text"
                  value={editForm.data.name}
                  onChange={e => editForm.setData('name', e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Step Type</label>
                <select
                  value={editForm.data.step_type}
                  onChange={e => editForm.setData('step_type', e.target.value)}
                  className="input w-full"
                >
                  {stepTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Description</label>
                <textarea
                  value={editForm.data.description}
                  onChange={e => editForm.setData('description', e.target.value)}
                  className="input w-full"
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.data.is_required}
                    onChange={e => editForm.setData('is_required', e.target.checked)}
                    className="rounded border-chestnut-charcoal/30"
                  />
                  <span className="text-sm text-chestnut-charcoal">Required step</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.data.is_active}
                    onChange={e => editForm.setData('is_active', e.target.checked)}
                    className="rounded border-chestnut-charcoal/30"
                  />
                  <span className="text-sm text-chestnut-charcoal">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-chestnut-charcoal/10">
                <button type="button" onClick={() => setEditingStep(null)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={editForm.processing}>
                  {editForm.processing && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function TemplatesTab({ 
  templates, 
  channel, 
  availableVariables,
  isConfigured,
  missingDefaults
}: { 
  templates: NotificationTemplate[]
  channel: 'email'
  availableVariables: string[]
  isConfigured: boolean
  missingDefaults: string[]
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null)

  const form = useForm({
    name: '',
    template_key: '',
    channel: channel,
    subject: '',
    body: '',
    is_active: true
  })

  const editForm = useForm({
    name: '',
    template_key: '',
    channel: channel as string,
    subject: '',
    body: '',
    is_active: true
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    form.post('/settings/workflows/templates', {
      onSuccess: () => {
        setIsAdding(false)
        form.reset()
      }
    })
  }

  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template)
    editForm.setData({
      name: template.name,
      template_key: template.template_key,
      channel: template.channel as 'email',
      subject: template.subject || '',
      body: template.body,
      is_active: template.is_active
    })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingTemplate) return
    editForm.patch(`/settings/workflows/templates/${editingTemplate.id}`, {
      onSuccess: () => {
        setEditingTemplate(null)
        editForm.reset()
      }
    })
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this template?')) {
      router.delete(`/settings/workflows/templates/${id}`)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-chestnut-charcoal">
            Email Templates
          </h2>
          <p className="text-sm text-chestnut-charcoal/60">
            Create and manage email templates for notifications
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link href="/admin/email_tests" className="btn-secondary flex items-center gap-2">
            <Send className="h-4 w-4" />
            Test system emails
          </Link>
          <button onClick={() => setIsAdding(true)} className="btn-primary flex items-center gap-2" disabled={!isConfigured}>
            <Plus className="h-4 w-4" />
            Add Template
          </button>
        </div>
      </div>

      <div className="bg-white border border-chestnut-charcoal/10 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-chestnut-charcoal">Default templates</h3>
          <p className="text-sm text-chestnut-charcoal/60">
            {missingDefaults.length === 0
              ? 'All default templates are installed. Your edits to them are never overwritten.'
              : `Missing: ${missingDefaults.join(', ')}. Installing adds only what is missing — existing templates are left exactly as they are.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => router.post('/settings/workflows/templates/install_defaults')}
          className="btn-secondary flex items-center gap-2"
          disabled={missingDefaults.length === 0}
        >
          <Download className="h-4 w-4" />
          Install default templates
        </button>
      </div>

      {!isConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-700">
            SendGrid is not configured. Please set up the integration in Settings before creating templates.
          </p>
        </div>
      )}

      <div className="bg-chestnut-cream/30 rounded-lg p-4">
        <h3 className="text-sm font-medium text-chestnut-charcoal mb-2">Available Variables</h3>
        <div className="flex flex-wrap gap-2">
          {availableVariables.map(v => (
            <code key={v} className="text-xs px-2 py-1 bg-white rounded border border-chestnut-charcoal/10">
              {'{{' + v + '}}'}
            </code>
          ))}
        </div>
      </div>

      {isAdding && (
        <form onSubmit={handleCreate} className="bg-chestnut-cream/50 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Template Name</label>
              <input
                type="text"
                value={form.data.name}
                onChange={e => form.setData('name', e.target.value)}
                className="input w-full"
                placeholder="e.g., Welcome Email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Template Key</label>
              <input
                type="text"
                value={form.data.template_key}
                onChange={e => form.setData('template_key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                className="input w-full"
                placeholder="e.g., welcome_email"
              />
            </div>
          </div>
          {channel === 'email' && (
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Subject</label>
              <input
                type="text"
                value={form.data.subject}
                onChange={e => form.setData('subject', e.target.value)}
                className="input w-full"
                placeholder="e.g., Welcome to {{company_name}}!"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
              Email Body
            </label>
            <textarea
              value={form.data.body}
              onChange={e => form.setData('body', e.target.value)}
              className="input w-full"
              rows={6}
              placeholder="Hi {{agent_first_name}},\n\nWelcome to {{company_name}}! ..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setIsAdding(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={form.processing}>Save Template</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {templates.length === 0 ? (
          <div className="text-center py-8 text-chestnut-charcoal/60">
            No {channel} templates yet. Create your first template to get started.
          </div>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className={`p-4 rounded-lg border ${
                template.is_active ? 'bg-white border-chestnut-charcoal/10' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-chestnut-charcoal">{template.name}</span>
                    <code className="text-xs px-2 py-0.5 bg-chestnut-cream rounded">{template.template_key}</code>
                    {!template.is_active && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">Inactive</span>
                    )}
                  </div>
                  {template.subject && (
                    <p className="text-sm text-chestnut-charcoal/70 mt-1">Subject: {template.subject}</p>
                  )}
                  <p className="text-sm text-chestnut-charcoal/60 mt-1 line-clamp-2">{template.body}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleEdit(template)}
                    className="p-2 text-chestnut-charcoal/40 hover:text-chestnut-forest rounded-lg hover:bg-chestnut-cream/50"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(template.id)} className="p-2 text-chestnut-charcoal/40 hover:text-red-600 rounded-lg hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-chestnut-charcoal/10">
              <h3 className="text-lg font-semibold text-chestnut-charcoal">
                Edit Email Template
              </h3>
              <button onClick={() => setEditingTemplate(null)} className="p-1 text-chestnut-charcoal/40 hover:text-chestnut-charcoal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Template Name</label>
                  <input
                    type="text"
                    value={editForm.data.name}
                    onChange={e => editForm.setData('name', e.target.value)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Template Key</label>
                  <input
                    type="text"
                    value={editForm.data.template_key}
                    onChange={e => editForm.setData('template_key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    className="input w-full"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Subject</label>
                <input
                  type="text"
                  value={editForm.data.subject}
                  onChange={e => editForm.setData('subject', e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Email Body
                </label>
                <textarea
                  value={editForm.data.body}
                  onChange={e => editForm.setData('body', e.target.value)}
                  className="input w-full"
                  rows={8}
                />
              </div>
              <div className="bg-chestnut-cream/30 rounded-lg p-3">
                <h4 className="text-xs font-medium text-chestnut-charcoal mb-2">Available Variables</h4>
                <div className="flex flex-wrap gap-1">
                  {availableVariables.map(v => (
                    <code key={v} className="text-xs px-1.5 py-0.5 bg-white rounded border border-chestnut-charcoal/10">
                      {'{{' + v + '}}'}
                    </code>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.data.is_active}
                    onChange={e => editForm.setData('is_active', e.target.checked)}
                    className="rounded border-chestnut-charcoal/30"
                  />
                  <span className="text-sm text-chestnut-charcoal">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-chestnut-charcoal/10">
                <button type="button" onClick={() => setEditingTemplate(null)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={editForm.processing}>
                  {editForm.processing && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function AutomationsTab({ 
  rules, 
  triggerTypes,
  templates
}: { 
  rules: AutomationRule[]
  triggerTypes: TriggerType[]
  templates: NotificationTemplate[]
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null)

  const form = useForm({
    name: '',
    description: '',
    trigger_type: 'agent_invited',
    channel: 'email',
    notification_template_id: '',
    timing: 'immediate',
    offset_days: 0,
    is_active: true
  })

  const editForm = useForm({
    name: '',
    description: '',
    trigger_type: '',
    channel: '',
    notification_template_id: '',
    timing: 'immediate',
    offset_days: 0,
    is_active: true
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    form.post('/settings/workflows/rules', {
      onSuccess: () => {
        setIsAdding(false)
        form.reset()
      }
    })
  }

  const handleEdit = (rule: AutomationRule) => {
    setEditingRule(rule)
    editForm.setData({
      name: rule.name,
      description: rule.description || '',
      trigger_type: rule.trigger_type,
      channel: rule.channel,
      notification_template_id: rule.notification_template_id?.toString() || '',
      timing: rule.timing,
      offset_days: rule.offset_days,
      is_active: rule.is_active
    })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingRule) return
    editForm.patch(`/settings/workflows/rules/${editingRule.id}`, {
      onSuccess: () => {
        setEditingRule(null)
        editForm.reset()
      }
    })
  }

  const handleToggle = (id: number) => {
    router.post(`/settings/workflows/rules/${id}/toggle`)
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this automation?')) {
      router.delete(`/settings/workflows/rules/${id}`)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-chestnut-charcoal">Automation Rules</h2>
          <p className="text-sm text-chestnut-charcoal/60">
            Set up automatic notifications based on events and triggers
          </p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Automation
        </button>
      </div>

      {isAdding && (
        <form onSubmit={handleCreate} className="bg-chestnut-cream/50 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Rule Name</label>
              <input
                type="text"
                value={form.data.name}
                onChange={e => form.setData('name', e.target.value)}
                className="input w-full"
                placeholder="e.g., Welcome New Agent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Trigger</label>
              <select
                value={form.data.trigger_type}
                onChange={e => form.setData('trigger_type', e.target.value)}
                className="input w-full"
              >
                {triggerTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Description</label>
            <input
              type="text"
              value={form.data.description}
              onChange={e => form.setData('description', e.target.value)}
              className="input w-full"
              placeholder="Optional description"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Channel</label>
              <select
                value={form.data.channel}
                onChange={e => form.setData('channel', e.target.value)}
                className="input w-full"
              >
                <option value="email">Email</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Timing</label>
              <select
                value={form.data.timing}
                onChange={e => form.setData('timing', e.target.value)}
                className="input w-full"
              >
                <option value="immediate">Immediately</option>
                <option value="delayed">Delayed</option>
                <option value="scheduled">Before Event</option>
              </select>
            </div>
            {form.data.timing !== 'immediate' && (
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Days</label>
                <input
                  type="number"
                  value={form.data.offset_days}
                  onChange={e => form.setData('offset_days', parseInt(e.target.value))}
                  className="input w-full"
                  min="0"
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Use Template</label>
            <select
              value={form.data.notification_template_id}
              onChange={e => form.setData('notification_template_id', e.target.value)}
              className="input w-full"
            >
              <option value="">Select a template...</option>
              {templates.filter(t => t.channel === form.data.channel).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setIsAdding(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={form.processing}>Save Automation</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {rules.length === 0 ? (
          <div className="text-center py-8 text-chestnut-charcoal/60">
            No automation rules yet. Create your first automation to get started.
          </div>
        ) : (
          rules.map((rule) => (
            <div
              key={rule.id}
              className={`p-4 rounded-lg border ${
                rule.is_active ? 'bg-white border-chestnut-charcoal/10' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-chestnut-charcoal">{rule.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      Email
                    </span>
                    {!rule.is_active && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-chestnut-charcoal/70 mt-1">
                    Trigger: {rule.trigger_label} • {rule.timing_description}
                  </p>
                  {rule.notification_template_name && (
                    <p className="text-sm text-chestnut-charcoal/60 mt-0.5">
                      Template: {rule.notification_template_name}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(rule.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      rule.is_active 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-chestnut-charcoal/40 hover:bg-chestnut-cream/50'
                    }`}
                    title={rule.is_active ? 'Disable' : 'Enable'}
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => handleEdit(rule)}
                    className="p-2 text-chestnut-charcoal/40 hover:text-chestnut-forest rounded-lg hover:bg-chestnut-cream/50"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(rule.id)} className="p-2 text-chestnut-charcoal/40 hover:text-red-600 rounded-lg hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editingRule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-chestnut-charcoal/10">
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Edit Automation Rule</h3>
              <button onClick={() => setEditingRule(null)} className="p-1 text-chestnut-charcoal/40 hover:text-chestnut-charcoal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Rule Name</label>
                <input
                  type="text"
                  value={editForm.data.name}
                  onChange={e => editForm.setData('name', e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Description</label>
                <input
                  type="text"
                  value={editForm.data.description}
                  onChange={e => editForm.setData('description', e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Trigger</label>
                <select
                  value={editForm.data.trigger_type}
                  onChange={e => editForm.setData('trigger_type', e.target.value)}
                  className="input w-full"
                >
                  {triggerTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Channel</label>
                  <select
                    value={editForm.data.channel}
                    onChange={e => editForm.setData('channel', e.target.value)}
                    className="input w-full"
                  >
                    <option value="email">Email</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Timing</label>
                  <select
                    value={editForm.data.timing}
                    onChange={e => editForm.setData('timing', e.target.value)}
                    className="input w-full"
                  >
                    <option value="immediate">Immediately</option>
                    <option value="delayed">Delayed</option>
                    <option value="scheduled">Before Event</option>
                  </select>
                </div>
              </div>
              {editForm.data.timing !== 'immediate' && (
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Offset Days</label>
                  <input
                    type="number"
                    value={editForm.data.offset_days}
                    onChange={e => editForm.setData('offset_days', parseInt(e.target.value))}
                    className="input w-full"
                    min="0"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Template</label>
                <select
                  value={editForm.data.notification_template_id}
                  onChange={e => editForm.setData('notification_template_id', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Select a template...</option>
                  {templates.filter(t => t.channel === editForm.data.channel).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.data.is_active}
                    onChange={e => editForm.setData('is_active', e.target.checked)}
                    className="rounded border-chestnut-charcoal/30"
                  />
                  <span className="text-sm text-chestnut-charcoal">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-chestnut-charcoal/10">
                <button type="button" onClick={() => setEditingRule(null)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={editForm.processing}>
                  {editForm.processing && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
